import React from "react";
import { motion } from "framer-motion";
import { Phone, MapPin, Clock, Navigation } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import {
  PHONE,
  PHONE_RAW,
  MAPS_EMBED_URL,
  MAPS_DIRECTIONS_URL,
} from "../lib/data";

export const Contact = () => {
  const { t } = useLanguage();
  const hours = [
    t.contact.hours.spring,
    t.contact.hours.summer,
    t.contact.hours.autumn,
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="max-w-2xl">
          <span
            data-testid="contact-eyebrow"
            className="inline-flex text-[11px] tracking-[0.2em] uppercase text-sky-700 font-semibold rounded-full bg-sky-100 px-4 py-1.5"
          >
            {t.contact.eyebrow}
          </span>
          <h2
            data-testid="contact-title"
            className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-slate-900"
          >
            {t.contact.title}
          </h2>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-6 md:gap-8">
          {/* Info column */}
          <div className="lg:col-span-5 flex flex-col gap-5">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="rounded-3xl bg-gradient-to-br from-sky-600 to-sky-800 text-white p-7 md:p-8 shadow-[0_20px_50px_rgba(2,132,199,0.25)] relative overflow-hidden"
              data-testid="contact-phone-card"
            >
              <div className="absolute -bottom-12 -right-8 w-40 h-40 rounded-full bg-yellow-300/30 blur-2xl" />
              <div className="text-[11px] uppercase tracking-[0.2em] text-sky-100/80 font-semibold">
                {t.contact.phoneLabel}
              </div>
              <a
                href={`tel:${PHONE_RAW}`}
                className="block mt-3 font-heading text-3xl md:text-4xl font-semibold tracking-tight"
              >
                {PHONE}
              </a>
              <a
                href={`tel:${PHONE_RAW}`}
                data-testid="contact-call-cta"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-slate-900 px-5 py-3 text-sm font-semibold shadow-[0_10px_24px_rgba(250,204,21,0.4)] hover:-translate-y-0.5 transition-all"
              >
                <Phone className="w-4 h-4" strokeWidth={2.5} />
                {t.contact.callCta}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.07 }}
              className="rounded-3xl bg-sky-50/70 border border-sky-100 p-7 md:p-8"
              data-testid="contact-hours-card"
            >
              <div className="flex items-center gap-2 text-sky-700">
                <Clock className="w-4 h-4" strokeWidth={2.5} />
                <span className="text-[11px] uppercase tracking-[0.2em] font-semibold">
                  {t.contact.hoursTitle}
                </span>
              </div>
              <ul className="mt-4 divide-y divide-sky-100">
                {hours.map((h, i) => (
                  <li
                    key={i}
                    data-testid={`hours-row-${i}`}
                    className="py-3 flex items-center justify-between gap-3"
                  >
                    <span className="font-heading font-medium text-slate-900 text-sm md:text-base">
                      {h.range}
                    </span>
                    <span className="text-slate-600 text-sm md:text-[15px] text-right">
                      {h.value}
                    </span>
                  </li>
                ))}
              </ul>
              <div
                data-testid="last-rental-note"
                className="mt-4 rounded-xl bg-white border border-yellow-200 px-4 py-3 text-xs md:text-[13px] text-slate-700 leading-snug flex items-start gap-2"
              >
                <span className="mt-0.5 w-1.5 h-1.5 shrink-0 rounded-full bg-yellow-400" />
                <span>{t.contact.lastRentalNote}</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.14 }}
              className="rounded-3xl bg-white border border-sky-100 p-7 md:p-8 shadow-sm"
              data-testid="contact-address-card"
            >
              <div className="flex items-center gap-2 text-sky-700">
                <MapPin className="w-4 h-4" strokeWidth={2.5} />
                <span className="text-[11px] uppercase tracking-[0.2em] font-semibold">
                  {t.contact.addressLabel}
                </span>
              </div>
              <p className="mt-3 text-slate-900 font-medium whitespace-pre-line leading-relaxed">
                {t.contact.addressValue}
              </p>
              <a
                href={MAPS_DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-directions-cta"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white px-5 py-3 text-sm font-semibold hover:-translate-y-0.5 transition-all"
              >
                <Navigation className="w-4 h-4 text-yellow-300" strokeWidth={2.5} />
                {t.contact.directionsCta}
              </a>
            </motion.div>
          </div>

          {/* Map column */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 rounded-3xl overflow-hidden border border-sky-100 shadow-[0_20px_50px_rgba(2,132,199,0.12)] min-h-[460px] lg:min-h-full"
            data-testid="contact-map"
          >
            <iframe
              title="Pasohlávky map"
              src={MAPS_EMBED_URL}
              className="w-full h-full min-h-[460px]"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
